import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { onboardingAdmin } from 'further-ui/validations';
import { z } from 'zod';

type UpdateOnboardingSettingsInput = z.infer<
  typeof onboardingAdmin.SaveOnboardingSettingsInput
>;

export default function useOnboardingSettings(vehicleId: string) {
  const queryClient = useQueryClient();
  const [onboardingSettings] = api.vehicle.onboardingSettings.useSuspenseQuery({
    vehicleId,
  });

  const queryKey = getQueryKey(
    api.vehicle.onboardingSettings,
    { vehicleId },
    'query',
  );

  const { mutate, isPending } =
    api.onboarding.updateOnboardingSettings.useMutation({
      ...useHandleTrpcMutation()({
        invalidations: [queryKey],
      }),
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData(queryKey, variables);
      },
    });

  const updateOnboardingSettings = (
    data: Partial<Omit<UpdateOnboardingSettingsInput, 'vehicleId'>>,
  ) => {
    mutate({
      vehicleId,
      ...onboardingSettings,
      ...data,
    });
  };

  return {
    onboardingSettings,
    updateOnboardingSettings,
    isUpdating: isPending,
  };
}
